import React, {useState, useRef, useEffect, useMemo, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {Form, Formik} from "formik";
import lodash from "lodash";
//local
import {
  IconDocumentPen,
  IconUserPlus,
  IconChevronDown,
  IconMessage,
} from "components/icons";
import Select from "components/selects/Select";
import {getStorage, setStorage, removeStorage} from "helpers/storage";
import EditDatePicker from "components/EditDatePicker";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import LoadDocsColumn from "components/LoadDocsColumn";
import SmartFormAnswers from "pages/OurRequests/components/SmartForm/SmartFormAnswers";
import {showSuccess} from "helpers/notifications.helper";
import {LockValue} from "components/LockValue";
import {uploadFiles, removeFilesConfirmModal, hasPermission} from "helpers/helper";
import {commonFileExtensions, fileExtension} from "constants/file.consts";
import {schema} from "./formAttrs";
import FormikField from "components/FormikField";

const OfferCardEdit = ({
  offer,
  offerFiles,
  setEdit,
  setCreate,
  loading,
  mode,
  sideData,
  uploadFile,
  updateDocuments,
  submit,
}) => {
  const formRef = useRef();
  const {t} = useTranslation();

  const getStorageRequestOffer = useCallback(
    () => JSON.parse(getStorage(`requestOffer-${offer?.requestId}`)),
    [offer?.requestId]
  );

  const [uploadLoading, setUploadLoading] = useState(false);
  const [requiredFilled, setRequiredFilled] = useState(false);
  const [initialOfferFiles] = useState(offerFiles);
  // seller request from draft (localStorage)
  const [storageRequestOffer, setStorageRequestOffer] = useState(
    getStorageRequestOffer()
  );
  const isCreateMode = useMemo(() => mode === "create", [mode]);
  const isEditMode = useMemo(() => mode === "edit", [mode]);

  // question without headers (only relevant)
  const questions = useMemo(
    () => offer?.smartFormQuestions?.filter((item) => item.type !== "heading") ?? [],
    [offer?.smartFormQuestions]
  );
  // are there any mandatory questions to answer
  const isRequiredQuestions = useMemo(
    () => !!questions?.filter((item) => item.required).length,
    [questions]
  );
  // answers from draft (localStorage)
  const storageRequestOfferAnswers = useMemo(
    () => storageRequestOffer?.smartFormAnswers,
    [storageRequestOffer]
  );
  const files = useMemo(
    () => storageRequestOffer?.OfferFiles ?? offerFiles ?? [],
    [offerFiles, storageRequestOffer]
  );

  const setStorageOfferFiles = useCallback(
    (values) =>
      setStorage(
        `requestOffer-${offer?.requestId}`,
        JSON.stringify({
          ...storageRequestOffer,
          OfferFiles: values,
        })
      ),
    [storageRequestOffer, offer?.requestId]
  );

  // empty answer object
  const emptyAnswer = useCallback(
    (id) => ({id: window.crypto.randomUUID(), questionId: id, value: null}),
    []
  );

  // adding answers that are missing in the draft due to new questions
  const answersForNewQuestions = useCallback(
    (q, a) =>
      q
        ?.filter((item) => !a.map((x) => x?.questionId)?.includes(item.id))
        .map((item) => emptyAnswer(item.id)),
    [emptyAnswer]
  );

  const initialAnswers = useMemo(
    () =>
      storageRequestOfferAnswers
        ? [
            ...storageRequestOfferAnswers,
            ...answersForNewQuestions(questions, storageRequestOfferAnswers),
          ]
        : questions?.map(
            (item) =>
              offer?.smartFormAnswers?.find((answer) => answer.questionId === item.id) ??
              emptyAnswer(item.id)
          ),
    [
      storageRequestOfferAnswers,
      answersForNewQuestions,
      emptyAnswer,
      offer?.smartFormAnswers,
      questions,
    ]
  );

  const [answers, setAnswers] = useState(initialAnswers);

  // getting the initial values of the offer fields from the draft
  const getOfferFieldValue = useCallback(
    (field) => storageRequestOffer?.[field],
    [storageRequestOffer]
  );

  const handleSubmit = useCallback(
    (values) => {
      const payload = {
        ...values,
        smartFormAnswers: answers,
        documents:
          files
            ?.filter((item) => !!item.File && !item.deletedAt)
            .map((item) => item.File?.id) ?? [],
      };
      submit(payload);
    },
    [answers, files, submit]
  );

  const addFileHandler = useCallback(
    (values) =>
      uploadFiles(
        values,
        uploadFile,
        (data) => {
          updateDocuments([...(files ?? []), ...data]);
          setStorageOfferFiles([...(files ?? []), ...data]);
          setStorageRequestOffer(getStorageRequestOffer());
        },
        setUploadLoading
      ),
    [files, uploadFile, updateDocuments, setStorageOfferFiles, getStorageRequestOffer]
  );

  const removeDocument = useCallback(
    (id) => {
      const docs = id ? files?.filter((item) => item?.id !== id) : [];
      updateDocuments(docs);
      showSuccess(t(id ? "FILE_DELETED" : "ALL_FILES_DELETED"));
      setStorageOfferFiles(docs);
      setStorageRequestOffer(getStorageRequestOffer());
    },
    [updateDocuments, setStorageOfferFiles, files, getStorageRequestOffer, t]
  );

  const removeFileHandler = (id) => removeFilesConfirmModal(id, removeDocument);

  const handleCancel = useCallback(() => {
    setEdit(false);
    setCreate();
    removeStorage(`requestOffer-${offer?.requestId}`);
    initialOfferFiles?.length && updateDocuments(initialOfferFiles);
  }, [updateDocuments, initialOfferFiles, offer?.requestId, setEdit, setCreate]);

  const setSmartFormAnswers = useCallback(
    (answers) => {
      setAnswers(answers);
      // add data to cache
      const debouncedSetStorage = lodash.debounce((requestId, data) => {
        setStorage(`requestOffer-${requestId}`, JSON.stringify(data));
      }, 300);

      debouncedSetStorage(offer?.requestId, {
        ...storageRequestOffer,
        smartFormAnswers: answers,
      });
    },
    [offer?.requestId, storageRequestOffer]
  );

  // write offer draft in the localStorage
  useEffect(() => {
    const writeDraft = setInterval(() => {
      const storageOffer = getStorageRequestOffer();
      setStorage(
        `requestOffer-${offer?.requestId}`,
        JSON.stringify({
          ...storageOffer,
          ...formRef?.current?.values,
        })
      );
      setStorageRequestOffer(getStorageRequestOffer());
    }, 1000);
    return () => {
      clearInterval(writeDraft);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="request-to-us_single offer-card_edit  __wrapper">
      <Formik
        initialValues={{
          price: getOfferFieldValue("price") ?? offer?.price,
          validUntil: getOfferFieldValue("validUntil") ?? offer?.validUntil,
          message: getOfferFieldValue("message") ?? offer?.message,
          UserAssigneeId: offer?.UserAssignee?.id,
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {({values, setFieldValue}) => (
          <Form>
            <div
              className={`offer-card_edit __actions${sideData.editButtonWrapperClass}`}
            >
              <LockValue
                canPermissions={[sideData.editPermission]}
                needLabel
                needShowModalToSubscribe
                addClassIcon="mr-3 ml-0"
              >
                <ButtonTransparent
                  type="submit"
                  name={sideData.labels.submitButton}
                  disabled={
                    uploadLoading ||
                    !(
                      !!values.price &&
                      !!values.validUntil &&
                      (!isRequiredQuestions || requiredFilled)
                    )
                  }
                  loading={loading}
                  nonTransparent
                />
              </LockValue>

              <ButtonTransparent
                disabled={uploadLoading || loading}
                onClick={handleCancel}
                name="CANCEL"
              />

              <span className="offer-card_edit __actions__status-draft">
                {t("OUR_OFFER_EDIT_DRAFT_SAVED")}
              </span>
            </div>
            <div className="mb-6">
              {offer?.organizationName && (
                <div className="offer-card-view_info __item __name">
                  <h6>{t("OFFER_FROM")}</h6>
                  {offer?.organizationName}
                </div>
              )}
            </div>
            <div className="df-row-center mb-3">
              <FormikField
                name="price"
                label={`${t(sideData.labels?.price)}, ${offer?.CurrencyCode}`}
                placeholder={t(sideData.labels?.pricePlaceholder)}
                small
                type="number"
                className="input"
                height="40px"
                highlightIfEmpty={!values.price}
              />
              <EditDatePicker
                label={t(sideData.labels.validUntil)}
                addClass="ml-8 mb-5"
                name="validUntil"
                hideErrorMessage
                small
                highlightIfEmpty={!values.validUntil}
                placeholder={sideData.labels.validUntilPlaceholder}
              />
            </div>

            {!!sideData.organizationMembers && (
              <LockValue
                canPermissions={[sideData.editPermission]}
                needLabel
                needShowModalToSubscribe
                addClassIcon="mb-1 ml-1"
              >
                <Select
                  title={sideData.labels.contact}
                  prefixIcon={<IconUserPlus />}
                  onChange={(value) => setFieldValue("UserAssigneeId", value)}
                  list={sideData.organizationMembers}
                  shadow
                  showArrow
                  tinyTitle
                  huge
                  initials
                  highlightIfEmpty={!values?.UserAssigneeId}
                  suffixIcon={<IconChevronDown />}
                  placeholder="USER_ASSIGNEE_SELECT_INITIAL_VALUE"
                  disabled={!hasPermission([sideData.editPermission])}
                  value={values?.UserAssigneeId}
                  addClass="mb-8"
                />
              </LockValue>
            )}

            <h3 className="offer-card-view_message __title">
              <IconMessage addClass="mr-1" />
              {t(sideData.description.title)}
            </h3>
            <FormikField
              name="message"
              placeholder={t(sideData.description.placeholder)}
              autoSize={{maxRows: 10}}
              maxLength="1000"
              textarea
              height="100px"
              textareaValue={values.message}
            />
            {!!questions?.length && (
              <SmartFormAnswers
                isNew={isCreateMode}
                isEdit={isEditMode}
                isSellerSide
                isRequiredFilled={!!questions?.length && setRequiredFilled}
                titleHide={!offer?.smartFormQuestions?.length}
                questions={offer?.smartFormQuestions}
                answers={answers}
                onChange={setSmartFormAnswers}
              />
            )}
            <div className="load-docs_wrapper offer-card_edit __documents">
              <div className="load-docs_column-load">
                <LoadDocsColumn
                  title={sideData.labels.documents}
                  docPlaceholder={sideData.labels.documentsPlaceholder}
                  titleIcon={<IconDocumentPen />}
                  button="ADD_DOCUMENT"
                  editable
                  docs={files.filter((file) => !file?.deletedAt)}
                  fullWidth
                  addFile={addFileHandler}
                  removeFile={removeFileHandler}
                  canRemoveAllFiles
                  loadColumnTitle={sideData.labels.documents}
                  customFileTypes={[
                    ...commonFileExtensions,
                    fileExtension.MSG,
                    fileExtension.EML,
                  ]}
                  loading={uploadLoading || loading}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default OfferCardEdit;
